import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Response interceptor — silently handle 401 on auth-check calls, retry once with refresh
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const url = original?.url || "";

    // If this is the initial auth check (/auth/me) or refresh call, just reject silently
    if (url.includes("/auth/me") || url.includes("/auth/refresh") || url.includes("/auth/login")) {
      return Promise.reject(error);
    }

    // For other 401s, try a token refresh once
    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        return api(original);
      } catch {
        // Refresh also failed — redirect to login
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  },
);

export default api;

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export const formatGBP = (amount) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    parseFloat(amount) || 0,
  );

export const formatDateUK = (dateStr) => {
  if (!dateStr) return "-";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) { const [y, m, dd] = dateStr.split("-"); return `${dd}/${m}/${y}`; }
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(dateStr) ? dateStr : `${dateStr}Z`);
  return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString("en-GB", { timeZone: "Europe/London" });
};

export const apiError = (err, fallback = "Failed to save") => {
  const d = err.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace(/^Value error, /, "");
  return fallback;
};

export const toInputDateUK = (iso) => (/^\d{4}-\d{2}-\d{2}$/.test(iso || "") ? formatDateUK(iso) : iso || "");

const UK_STAMP = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/London", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZoneName: "short",
});
export const formatStampUK = (iso) => {
  if (!iso) return "";
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
  return isNaN(d.getTime()) ? iso : UK_STAMP.format(d);
};

export const formatBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
};
